import { Link } from '@/lib/nav';
import { ArrowRight } from 'lucide-react';
import { Container, Section, Eyebrow } from '@/components/primitives/Container';
import { LayerDiagram, PipelineDiagram } from '@/components/diagrams/LayerDiagram';
import { AntiClaimBlock } from '@/components/citation/AntiClaimBlock';
import { ROUTES } from '@/lib/routes';

const ANTI_CLAIMS = [
  'No interpreta pedagógicamente — eso es del docente.',
  'No produce contenido pedagógico nuevo.',
  'No infiere relaciones que no estén validadas en el Core.',
  'No responde sin fuente.',
  'No reemplaza al docente, al directivo ni al especialista.',
];

const CAPABILITIES = [
  'Consultar el NAP de un nivel y un área específicos.',
  'Consultar el diseño jurisdiccional vigente de una provincia.',
  'Ver simultáneamente NAP nacional y diseño jurisdiccional.',
  'Comparar dos jurisdicciones para un mismo NAP.',
  'Auditar coherencia curricular institucional.',
  'Exponer todo lo anterior vía Model Context Protocol a un agente externo.',
];

export function Producto() {
  return (
    <>
      <section className="rule-bottom">
        <Container className="pt-16 pb-16 md:pt-24 md:pb-20">
          <Eyebrow className="mb-8">Producto</Eyebrow>
          <h1 className="max-w-5xl font-display text-5xl font-medium leading-[1.05] tracking-tight text-accent md:text-6xl lg:text-7xl">
            Una capa de inteligencia
            <br />
            <span className="font-normal italic">sobre la norma curricular.</span>
          </h1>
          <p className="mt-8 max-w-prose text-lg leading-relaxed text-ink-muted md:text-xl">
            No es un repositorio. No es un buscador. No es un asistente.
          </p>
        </Container>
      </section>

      <Section ariaLabelledby="layers-title" className="pt-0">
        <Container>
          <h2 id="layers-title" className="sr-only">Arquitectura de capas</h2>
          <LayerDiagram />
        </Container>
      </Section>

      <Section ariaLabelledby="how-title" className="bg-paper-tinted/40">
        <Container>
          <div className="mb-10 max-w-editorial">
            <Eyebrow>Cómo funciona</Eyebrow>
            <h2 id="how-title" className="mt-3 font-serif text-3xl font-normal tracking-tight">
              El proceso, en tres pasos.
            </h2>
          </div>
          <PipelineDiagram />
        </Container>
      </Section>

      <Section ariaLabelledby="capabilities-title">
        <Container width="editorial">
          <Eyebrow>Capacidades</Eyebrow>
          <h2 id="capabilities-title" className="mt-3 font-serif text-3xl font-normal tracking-tight">
            Lo que SICA puede hacer.
          </h2>
          <ul className="mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {CAPABILITIES.map((c) => (
              <li
                key={c}
                className="border-t border-line pt-4 text-base leading-relaxed text-ink"
              >
                {c}
              </li>
            ))}
          </ul>
        </Container>
      </Section>

      <Section ariaLabelledby="anti-title" className="border-t border-line">
        <Container width="editorial">
          <Eyebrow>Lo que SICA no hace, deliberadamente</Eyebrow>
          <h2 id="anti-title" className="mt-3 font-serif text-3xl font-normal tracking-tight">
            Las restricciones también son parte del producto.
          </h2>
          <AntiClaimBlock items={ANTI_CLAIMS} className="mt-8" />
        </Container>
      </Section>

      <Section ariaLabelledby="citations-title" className="bg-paper-tinted/40">
        <Container width="editorial">
          <Eyebrow>Política de citas</Eyebrow>
          <h2 id="citations-title" className="mt-3 font-serif text-3xl font-normal tracking-tight">
            Cero alucinaciones sobre la norma. Si no hay fuente, SICA dice que no sabe.
          </h2>
          <p className="prose-editorial mt-6">
            Toda respuesta incluye documento y página verificables. Esa restricción se llama Política
            de Citas y vive en el Core, no en la UI: ningún consumidor puede construir una respuesta
            sin asociarla a un fragmento del Inventario de Activos Curriculares.
          </p>
          <div className="mt-6">
            <Link
              to={ROUTES.producto.metodo}
              className="focus-ring inline-flex items-center gap-1 rounded text-sm font-medium text-ink hover:text-accent"
            >
              Ver el método completo
              <ArrowRight size={14} />
            </Link>
          </div>
        </Container>
      </Section>

      <Section ariaLabelledby="next-title">
        <Container>
          <h2 id="next-title" className="sr-only">Continuar</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <CrossLink
              eyebrow="Cobertura"
              title="Mapa federal"
              to={ROUTES.producto.jurisdicciones}
            />
            <CrossLink
              eyebrow="Rigor"
              title="Método de SICA"
              to={ROUTES.producto.metodo}
            />
            <CrossLink
              eyebrow="Integración"
              title="MCP para agentes IA"
              to={ROUTES.producto.mcp}
            />
          </div>
        </Container>
      </Section>
    </>
  );
}

function CrossLink({ eyebrow, title, to }: { eyebrow: string; title: string; to: string }) {
  return (
    <Link
      to={to}
      className="focus-ring group flex items-center justify-between rounded-lg border border-line bg-paper p-5 transition-colors hover:border-line_strong hover:bg-paper-tinted"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">{eyebrow}</p>
        <p className="mt-1 text-base font-medium text-ink">{title}</p>
      </div>
      <ArrowRight size={16} className="text-ink-muted transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}
